import { Component, ElementRef, EventEmitter, Output, ViewChild } from "@angular/core";
import { PAProject } from "../classes/project";
import { PATechnician } from "../classes/technician";
import { PAUtil, WeekDay } from "../classes/util";
import { PATimeControl } from "../singletons/pa-time-control";
import { PASettingsControl, SettingTab } from "../singletons/pa-settings-control";
import { PADataControl } from "../singletons/pa-data-control";
import { PAFilterControl } from "../singletons/pa-filter-control";
import { PAFilter } from "../classes/filter";

import { FormsModule } from "@angular/forms";
import { AdditionalPriorityTimeComponent } from "../reuseable-components/additional-priority-time/additional-priority-time.component";

@Component({
  selector: "hawk-pa-settings",
  templateUrl: "./pa-settings.component.html",
  styleUrls: [
    "./pa-settings.component.scss",
    "./../styles/common_styles.scss",
    "./../../_shared/styles/common-styles.scss",
  ],
  standalone: true,
  imports: [FormsModule, AdditionalPriorityTimeComponent],
})
export class PaSettingsComponent {
  @Output() updateWeekdaysEvent = new EventEmitter<WeekDay[]>();
  @ViewChild("settingsContent") settingsContent: ElementRef<HTMLDivElement>;

  protected readonly Project = PAProject;
  protected readonly Technician = PATechnician;

  // Startzeiten-Konfiguration
  showAddTechnicianDialog: boolean = false;
  technicianSearchFilter: string = "";
  onlyBentomaxTechnicians: boolean = true;

  constructor() {}

  // Scrollt zur angegebenen Sektion (zentriert im sichtbaren Bereich)
  scrollToSection(section: SettingTab): void {
    PASettingsControl.Instance.activeSettingsTab = section;
    const element = document.getElementById(`section-${section}`);
    if (element && this.settingsContent) {
      const container = this.settingsContent.nativeElement;

      // Berechne die Scroll-Position, um das Element vertikal zu zentrieren
      const elementTop = element.offsetTop;
      const elementHeight = element.offsetHeight;
      const containerHeight = container.clientHeight;
      const scrollTo = elementTop - containerHeight / 2 + elementHeight / 2;

      container.scrollTo({
        top: Math.max(0, scrollTo),
        behavior: "smooth",
      });
    }
  }

  // Aktualisiert die aktive Sektion beim Scrollen
  onScroll(event: Event): void {
    const container = event.target as HTMLElement;
    const sections = PASettingsControl.Instance.settingTabs;

    // Prüfe ob wir ganz oben sind
    const isAtTop = container.scrollTop < 50;
    if (isAtTop) {
      // Am Anfang: Ersten Menüpunkt aktivieren
      PASettingsControl.Instance.activeSettingsTab = sections[0];
      return;
    }

    // Prüfe ob wir am Ende des Scroll-Bereichs sind
    const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 50;
    if (isAtBottom) {
      // Am Ende: Letzten Menüpunkt aktivieren
      PASettingsControl.Instance.activeSettingsTab = sections[sections.length - 1];
      return;
    }

    // Finde die Sektion, die am nächsten zur Mitte des sichtbaren Bereichs ist
    const containerCenter = container.clientHeight / 2;
    let closestSection: SettingTab = sections[0];
    let closestDistance = Infinity;

    for (const section of sections) {
      const element = document.getElementById(`section-${section}`);
      if (element) {
        const rect = element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const elementCenter = rect.top - containerRect.top + rect.height / 2;
        const distance = Math.abs(elementCenter - containerCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestSection = section;
        }
      }
    }

    PASettingsControl.Instance.activeSettingsTab = closestSection;
  }

  // Gibt die Techniker-Startzeiten zurück, aber nur für aktive/geladene Techniker
  getActiveTechnicianStartTimes(): { user_id: number; start_minutes: number; is_arrival_time?: boolean }[] {
    const configs =
      PASettingsControl.Instance.selectedSettingConfig.data.tour_planning.work_start_times.technician_start_times;
    const loadedTechnicianIds = PAFilterControl.Instance.globalFilteredTechnicians.map((t) => t.id);
    return configs.filter((c) => loadedTechnicianIds.includes(c.user_id));
  }

  // Gibt den Namen eines Technikers anhand der ID zurück
  getTechnicianNameById(user_id: number): string {
    const technician = PAFilterControl.Instance.globalFilteredTechnicians.find((t) => t.id === user_id);
    return technician ? technician.getFullName() : `Techniker #${user_id}`;
  }

  // Gibt Techniker zurück, die noch keine individuelle Startzeit haben (gefiltert nach Suche und Bentomax-Filter)
  getFilteredTechniciansForAdd(): PATechnician[] {
    const existingIds =
      PASettingsControl.Instance.selectedSettingConfig.data.tour_planning.work_start_times.technician_start_times.map(
        (c) => c.user_id,
      );
    let technicians = PAFilterControl.Instance.globalFilteredTechnicians.filter((t) => !existingIds.includes(t.id));

    if (this.onlyBentomaxTechnicians) {
      technicians = PAFilter.filterTechniciansForBentomaxTechnicians(technicians);
    }

    if (this.technicianSearchFilter) {
      const filter = this.technicianSearchFilter.toLowerCase();
      technicians = technicians.filter((t) => t.getFullName().toLowerCase().includes(filter));
    }

    return technicians.sort((a, b) => a.getFullName().localeCompare(b.getFullName()));
  }

  // Fügt eine neue Techniker-Startzeit hinzu
  addTechnicianStartTime(technician_id: number): void {
    const default_minutes =
      PASettingsControl.Instance.selectedSettingConfig.data.tour_planning.work_start_times.default_start_minutes;
    PASettingsControl.Instance.setTechnicianStartMinutes(technician_id, default_minutes);
    this.showAddTechnicianDialog = false;
    this.technicianSearchFilter = "";
  }

  // Entfernt eine Techniker-Startzeit
  removeTechnicianStartTime(technician_id: number): void {
    PASettingsControl.Instance.removeTechnicianStartTime(technician_id);
  }

  // Konvertiert Time-Input-Wert zu Minuten und speichert
  onDefaultStartTimeChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    PASettingsControl.Instance.selectedSettingConfig.data.tour_planning.work_start_times.default_start_minutes =
      PASettingsControl.Instance.timeStringToMinutes(input.value);
  }

  // Konvertiert Time-Input-Wert zu Minuten für einen Techniker
  onTechnicianStartTimeChange(
    event: Event,
    config: { user_id: number; start_minutes: number; is_arrival_time?: boolean },
  ): void {
    const input = event.target as HTMLInputElement;
    config.start_minutes = PASettingsControl.Instance.timeStringToMinutes(input.value);
  }

  // Gibt die formatierte Standard-Startzeit zurück
  getDefaultStartTimeString(): string {
    return PASettingsControl.Instance.minutesToTimeString(
      PASettingsControl.Instance.selectedSettingConfig.data.tour_planning.work_start_times.default_start_minutes,
    );
  }

  // Gibt die formatierte Techniker-Startzeit zurück
  getTechnicianStartTimeString(config: { user_id: number; start_minutes: number; is_arrival_time?: boolean }): string {
    return PASettingsControl.Instance.minutesToTimeString(config.start_minutes);
  }

  // Toggle für Reiseziel-Optimierung Wochentage
  toggleOptimizeWeekday(weekday: WeekDay): void {
    const weekdays =
      PASettingsControl.Instance.selectedSettingConfig.data.travel_technicians.optimize_travel_locations.on_weekdays;
    const index = weekdays.indexOf(weekday);
    if (index > -1) {
      weekdays.splice(index, 1);
    } else {
      weekdays.push(weekday);
    }
  }

  public updateFilteredWeekdays(): void {
    this.updateWeekdaysEvent.emit(this.weekDayFilterToWeekdayStringList());
  }

  public weekDayFilterToWeekdayStringList(): WeekDay[] {
    const res = [];
    const show_weekday_config = PASettingsControl.Instance.selectedSettingConfig.data.general.show_weekdays;
    show_weekday_config.monday ? res.push("monday") : {};
    show_weekday_config.tuesday ? res.push("tuesday") : {};
    show_weekday_config.wednesday ? res.push("wednesday") : {};
    show_weekday_config.thursday ? res.push("thursday") : {};
    show_weekday_config.friday ? res.push("friday") : {};
    show_weekday_config.saturday ? res.push("saturday") : {};
    show_weekday_config.sunday ? res.push("sunday") : {};
    return res;
  }

  public toggleWeekday(weekday: string): void {
    const show_weekday_config = PASettingsControl.Instance.selectedSettingConfig.data.general.show_weekdays;
    switch (weekday) {
      case "mo": {
        show_weekday_config.monday = !show_weekday_config.monday;
        break;
      }
      case "di": {
        show_weekday_config.tuesday = !show_weekday_config.tuesday;
        break;
      }
      case "mi": {
        show_weekday_config.wednesday = !show_weekday_config.wednesday;
        break;
      }
      case "do": {
        show_weekday_config.thursday = !show_weekday_config.thursday;
        break;
      }
      case "fr": {
        show_weekday_config.friday = !show_weekday_config.friday;
        break;
      }
      case "sa": {
        show_weekday_config.saturday = !show_weekday_config.saturday;
        break;
      }
      case "so": {
        show_weekday_config.sunday = !show_weekday_config.sunday;
        break;
      }
    }
    this.updateFilteredWeekdays();
  }

  selectedWeekDaysToString(): string {
    const show_weekday_config = PASettingsControl.Instance.selectedSettingConfig.data.general.show_weekdays;
    const weekdays_groups = [];
    let connected = false;
    for (const week_day of PATimeControl.Instance.weekDays) {
      const filter_value = show_weekday_config[week_day];
      const element_count = weekdays_groups.length;
      if (connected && element_count) {
        if (filter_value) {
          weekdays_groups[element_count - 1].push(PATimeControl.Instance.weekDayToGermanName(week_day, true));
        } else {
          connected = false;
        }
      } else {
        if (filter_value) {
          weekdays_groups.push([PATimeControl.Instance.weekDayToGermanName(week_day, true)]);
          connected = true;
        }
      }
    }
    return weekdays_groups
      .map((weekday_group) =>
        weekday_group.length == 1 ? weekday_group[0] : `${weekday_group[0]}-${weekday_group[weekday_group.length - 1]}`,
      )
      .join(", ");
  }

  protected readonly PASettingsControl = PASettingsControl;
  protected readonly PAFilterControl = PAFilterControl;
  protected readonly PADataControl = PADataControl;
  protected readonly PAUtil = PAUtil;
  protected readonly PATimeControl = PATimeControl;
}
